import React from "react";
import { projectWorldToViewportMm } from "../../engine/viewProjection";


function Arrow({ x, y, direction = "left" }) {
  const size = 2.2;

  if (direction === "left") {
    return (
      <polygon
        points={`${x},${y} ${x + size},${y - size} ${x + size},${y + size}`}
        fill="#111827"
      />
    );
  }

  if (direction === "right") {
    return (
      <polygon
        points={`${x},${y} ${x - size},${y - size} ${x - size},${y + size}`}
        fill="#111827"
      />
    );
  }

  if (direction === "up") {
    return (
      <polygon
        points={`${x},${y} ${x - size},${y + size} ${x + size},${y + size}`}
        fill="#111827"
      />
    );
  }

  return (
    <polygon
      points={`${x},${y} ${x - size},${y - size} ${x + size},${y - size}`}
      fill="#111827"
    />
  );
}

function DimensionOffsetGrip({
  x,
  y,
  orientation,
  offsetMm,
  onPreview,
  onCommit,
  screenPxPerMm = 3,
}) {
  function handlePointerDown(e) {
    e.stopPropagation();
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;
    const startOffset = Number(offsetMm || 10);

    function getNextOffset(ev) {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      const deltaMm =
        orientation === "horizontal"
          ? dy / Number(screenPxPerMm || 3)
          : dx / Number(screenPxPerMm || 3);

      return Math.max(-200, Math.min(200, startOffset + deltaMm));
    }

    function move(ev) {
      onPreview?.(getNextOffset(ev));
    }

    function up(ev) {
      const nextOffset = getNextOffset(ev);

      onPreview?.(null);
      onCommit?.(nextOffset);

      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    }

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  return (
    <g
      onMouseDown={handlePointerDown}
      style={{
        cursor: orientation === "horizontal" ? "ns-resize" : "ew-resize",
      }}
    >
      <circle
        cx={x}
        cy={y}
        r={3}
        fill="#2563eb"
        stroke="white"
        strokeWidth={0.8}
      />
    </g>
  );
}



export default function DrawingDimension({
  annotation,
  viewport,
  view,
  selected = false,
  active = false,
  screenPxPerMm = 3,
  onSelect,
  onUpdate,
}) {
  const [previewOffsetMm, setPreviewOffsetMm] = React.useState(null);

  if (!annotation) return null;

  const committedOffset = Number(annotation.offsetMm || 10);
  const effectiveOffset = previewOffsetMm ?? committedOffset;

  if (annotation.ref1?.point && annotation.ref2?.point) {
    const p1 = projectWorldToViewportMm(annotation.ref1.point, view, viewport);
    const p2 = projectWorldToViewportMm(annotation.ref2.point, view, viewport);

    const offset = effectiveOffset;
    const orientation = annotation.orientation || "horizontal";
    const text = annotation.text || "";

if (orientation === "horizontal") {
  const baseY =
    offset >= 0
      ? Math.max(p1.y, p2.y)
      : Math.min(p1.y, p2.y);

  const y = baseY + offset;

  return (
  <g
    onMouseDown={(e) => {
      e.stopPropagation();
      onSelect?.();
    }}
    style={{ cursor: active ? "pointer" : "default" }}
  >
    <line
      x1={p1.x}
      y1={p1.y}
      x2={p1.x}
      y2={y}
      stroke="#111827"
      strokeWidth={0.25}
    />
    <line
      x1={p2.x}
      y1={p2.y}
      x2={p2.x}
      y2={y}
      stroke="#111827"
      strokeWidth={0.25}
    />

    <line
      x1={p1.x}
      y1={y}
      x2={p2.x}
      y2={y}
      stroke={selected ? "#2563eb" : "#111827"}
      strokeWidth={selected ? 0.65 : 0.35}
    />

    {/* hit area */}
    <line
      x1={p1.x}
      y1={y}
      x2={p2.x}
      y2={y}
      stroke="transparent"
      strokeWidth={6}
    />

    <Arrow x={p1.x} y={y} direction="left" />
    <Arrow x={p2.x} y={y} direction="right" />

    <text
      x={(p1.x + p2.x) / 2}
      y={y - 2}
      fontSize={3}
      textAnchor="middle"
      fill={selected ? "#2563eb" : "#111827"}
    >
      {text}
    </text>

   {selected ? (
  <DimensionOffsetGrip
  x={(p1.x + p2.x) / 2}
  y={y}
  orientation="horizontal"
  offsetMm={offset}
  screenPxPerMm={screenPxPerMm}
  onPreview={setPreviewOffsetMm}
  onCommit={(nextOffset) => {
    setPreviewOffsetMm(null);
    onUpdate?.({ offsetMm: nextOffset });
  }}
/>
) : null}
  </g>
);
    }

    const baseX =
  offset >= 0
    ? Math.max(p1.x, p2.x)
    : Math.min(p1.x, p2.x);

const x = baseX + offset;

return (
  <g
    onMouseDown={(e) => {
      e.stopPropagation();
      onSelect?.();
    }}
    style={{ cursor: active ? "pointer" : "default" }}
  >
        <line x1={p1.x} y1={p1.y} x2={x} y2={p1.y} stroke="#111827" strokeWidth={0.25} />
        <line x1={p2.x} y1={p2.y} x2={x} y2={p2.y} stroke="#111827" strokeWidth={0.25} />

        <line
  x1={x}
  y1={p1.y}
  x2={x}
  y2={p2.y}
  stroke={selected ? "#2563eb" : "#111827"}
  strokeWidth={selected ? 0.65 : 0.35}
/>

{selected ? (
  <DimensionOffsetGrip
  x={x}
  y={(p1.y + p2.y) / 2}
  orientation="vertical"
  offsetMm={offset}
  screenPxPerMm={screenPxPerMm}
  onPreview={setPreviewOffsetMm}
  onCommit={(nextOffset) => {
    setPreviewOffsetMm(null);
    onUpdate?.({ offsetMm: nextOffset });
  }}
/>
) : null}

        <Arrow x={x} y={p1.y} direction="up" />
        <Arrow x={x} y={p2.y} direction="down" />

        <text
          x={x - 3}
          y={(p1.y + p2.y) / 2}
          fontSize={3}
          textAnchor="middle"
          fill="#111827"
          transform={`rotate(-90 ${x - 3} ${(p1.y + p2.y) / 2})`}
        >
          {text}
        </text>
      </g>
    );
  }

  const {
    x1Mm = 10,
    y1Mm = 10,
    x2Mm = 60,
    y2Mm = 10,
    text = "",
    orientation = "horizontal",
  } = annotation;

  if (orientation === "vertical") {
    const textX = x1Mm - 3;
    const textY = (y1Mm + y2Mm) / 2;

    return (
      <g>
        <line x1={x1Mm} y1={y1Mm} x2={x2Mm} y2={y2Mm} stroke="#111827" strokeWidth={0.35} />
        <Arrow x={x1Mm} y={y1Mm} direction="up" />
        <Arrow x={x2Mm} y={y2Mm} direction="down" />
        <text
          x={textX}
          y={textY}
          fontSize={3}
          textAnchor="middle"
          fill="#111827"
          transform={`rotate(-90 ${textX} ${textY})`}
        >
          {text}
        </text>
      </g>
    );
  }

  const textX = (x1Mm + x2Mm) / 2;
  const textY = y1Mm - 2;

  return (
    <g>
      <line x1={x1Mm} y1={y1Mm} x2={x2Mm} y2={y2Mm} stroke="#111827" strokeWidth={0.35} />
      <Arrow x={x1Mm} y={y1Mm} direction="left" />
      <Arrow x={x2Mm} y={y2Mm} direction="right" />
      <text
        x={textX}
        y={textY}
        fontSize={3}
        textAnchor="middle"
        fill="#111827"
      >
        {text}
      </text>
    </g>
  );
}